import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { money, fmtDate, fmtDateTime } from "@/lib/format";

const STATUS = { active: "Active", partially_used: "Partially Used", redeemed: "Redeemed", voided: "Voided" };
const TONE = { active: "bg-[#5C8A72]/12 text-[#5C8A72]", partially_used: "bg-[#D98E73]/15 text-[#B5794E]", redeemed: "bg-[#144F75]/10 text-[#144F75]", voided: "bg-red-50 text-red-600" };

function Detail({ c, onClose }) {
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm grid place-items-center px-4" data-testid="pos-gc-detail">
      <div className="w-full max-w-lg bg-[#F9F7F3] rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between"><p className="font-mono text-lg tracking-widest text-[#144F75]">{c.code}</p><button onClick={onClose} data-testid="pos-gc-detail-close"><X size={18} /></button></div>
        <div className="grid grid-cols-2 gap-1.5 text-sm text-[#4A5D6B] mt-3">
          <p>Value <b className="text-[#1A242B]">{money(c.value)}</b></p><p>Balance <b className="text-[#1A242B]">{money(c.balance)}</b></p>
          <p>Status: {STATUS[c.status]}</p><p>Sold {fmtDate(c.sold_at)}</p>
          <p>Sold by: {c.sold_by_name || "—"}</p><p>Center: {c.location_name || "—"}</p>
          <p>Enhancer: {c.enhancer_name || "—"}</p><p>Purchaser: {c.purchaser_name || c.purchaser_email || "—"}</p>
          <p className="col-span-2">Recipient: {c.recipient_name || "—"} {c.recipient_email && `· ${c.recipient_email}`}</p>
        </div>
        <p className="eyw-label mt-4">Redemption history</p>
        {c.redemptions.length === 0 ? <p className="text-sm text-[#8FA89B]">Not used yet.</p> : (
          <ul className="divide-y divide-[#E5E0D8] text-sm" data-testid="pos-gc-redemptions">
            {c.redemptions.map((r, i) => <li key={i} className="py-2 flex justify-between gap-2"><span>{r.label || r.booking_key}<span className="block text-xs text-[#8FA89B]">{fmtDateTime(r.at)} · {r.by}</span></span><span className="text-right">{money(r.amount)}<span className="block text-xs text-[#8FA89B]">left {money(r.balance_after)}</span></span></li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

// Gift cards this register user may see: center sales (manager/staff), own sales (enhancer), all (admin).
export default function POSGiftCards() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const qs = new URLSearchParams(Object.entries({ q, status }).filter(([, v]) => v)).toString();
  const { data, isLoading } = useQuery({ queryKey: ["pos-u-gift-cards", qs], queryFn: () => api.get(`/pos/u/gift-cards?${qs}`).then((r) => r.data) });
  const cards = data?.cards || [];
  return (
    <div data-testid="pos-gift-cards">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-serif text-2xl text-[#1A242B]">Gift cards</h2><p className="text-sm text-[#4A5D6B]">Balances and redemption history update the moment a card is used.</p></div>
        <p className="font-serif text-xl text-[#144F75]" data-testid="pos-gc-outstanding">{money(data?.outstanding || 0)} unredeemed</p>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, purchaser or recipient" data-testid="pos-gc-search" className="eyw-input !py-2 text-sm flex-1 min-w-[200px]" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pos-gc-status" className="eyw-input !py-2 text-sm">
          <option value="">All statuses</option>{Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {isLoading ? <Loader2 className="animate-spin mt-8 text-[#144F75]" /> : cards.length === 0 ? <p className="eyw-card p-8 mt-5 text-center text-[#4A5D6B]" data-testid="pos-gc-empty">No gift cards here yet.</p> : (
        <div className="eyw-card mt-5 overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-[#8FA89B] bg-[#F9F7F3]"><th className="py-2.5 px-4">Code</th><th>Status</th><th className="text-right">Value</th><th className="text-right">Balance</th><th>Purchaser</th><th>Sold by</th><th className="px-4">Sold</th></tr></thead>
          <tbody>{cards.map((c) => (
            <tr key={c.id} onClick={() => setOpen(c)} data-testid={`pos-gc-row-${c.code}`} className="border-t border-[#E5E0D8] cursor-pointer hover:bg-[#144F75]/[0.03]">
              <td className="py-2.5 px-4 font-mono">{c.code}</td><td><span className={`text-xs rounded-full px-2 py-0.5 ${TONE[c.status]}`}>{STATUS[c.status]}</span></td>
              <td className="text-right">{money(c.value)}</td><td className="text-right font-medium">{money(c.balance)}</td>
              <td className="text-xs">{c.purchaser_name || c.purchaser_email || "—"}</td><td className="text-xs">{c.sold_by_name || "—"}</td><td className="text-xs px-4 whitespace-nowrap">{fmtDate(c.sold_at)}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      {open && <Detail c={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
